import { memo } from 'react';
import { ServiceProvider, ServiceProviderSubstitute } from 'models';
import { format, startOfDay } from 'date-fns';

import { useStyles } from './style';
import clsx from 'clsx';

const filterRestricted = (str: string | null) => {
  if (str) str = str.trim();
  return !str || !str.length || str.match(new RegExp('RESTRICTED', 'g'))
    ? null
    : str;
};

const shouldShowSubstitute = (substitutes: ServiceProviderSubstitute[]) => {
  if (!substitutes.length) return false;

  let [substitute] = substitutes;

  const date = startOfDay(new Date());
  const startDate = substitute.Start
    ? startOfDay(new Date(substitute.Start))
    : null;
  const endDate = substitute.End ? startOfDay(new Date(substitute.End)) : null;

  if (!endDate && !startDate) return true;

  if (startDate && !endDate) {
    return date >= startDate;
  }

  if (!startDate && endDate) {
    return date <= endDate;
  }

  if (startDate && endDate) {
    if (date < startDate) {
      return false;
    }

    if (date > endDate) {
      return false;
    }

    return date >= startDate || endDate >= date;
  }
};

interface Props {
  provider: ServiceProvider | ServiceProviderSubstitute;
  substitute?: boolean;
}

const ProviderInfo: React.FC<Props> = ({ provider, substitute = false }) => {
  const classes = useStyles();

  let showSubstitute = shouldShowSubstitute(provider.Substitutes);

  return (
    <div
      className={clsx(classes.root, {
        [classes.substitute]: substitute,
      })}
    >
      {substitute ? (
        <span className={classes.substituteTitle}>Sijainen</span>
      ) : null}
      <h4 className={classes.title}>
        {showSubstitute
          ? provider.Substitutes.length && provider.Substitutes[0].End
            ? `${provider.Name} (Lomalla ${format(
                new Date(provider.Substitutes[0].End),
                'd.M.yyyy'
              )} saakka)`
            : `${provider.Name} (Lomalla)`
          : provider.Name}
      </h4>
      <ul className={classes.list}>
        {filterRestricted(provider.Email) ? (
          <li>
            <a
              className={clsx(classes.listItemText, classes.link)}
              href={`mailto:${provider.Email}`}
            >
              {provider.Email}
            </a>
          </li>
        ) : null}
        {filterRestricted(provider.MobilePhoneNumber) ? (
          <li>
            <a
              className={clsx(
                classes.listItemText,
                classes.phoneNumber,
                classes.link
              )}
              href={`tel:${provider.MobilePhoneNumber}`}
            >
              {provider.MobilePhoneNumber}
            </a>
            <span className={clsx(classes.listItemText, classes.text)}>
              {provider.MobilePhoneNumberDescription}
            </span>
          </li>
        ) : null}
        {provider.EmergencyPhoneNumber ? (
          <li>
            <a
              className={clsx(
                classes.listItemText,
                classes.phoneNumber,
                classes.link
              )}
              href={`tel:${provider.EmergencyPhoneNumber}`}
            >
              {provider.EmergencyPhoneNumber} {'päivystys'}
            </a>
            <span className={clsx(classes.listItemText, classes.text)}>
              {provider.EmergencyPhoneNumberDescription}
            </span>
          </li>
        ) : null}
        {filterRestricted(provider.PhoneNumber) ? (
          <li>
            <a
              className={clsx(
                classes.listItemText,
                classes.phoneNumber,
                classes.link
              )}
              href={`tel:${provider.PhoneNumber}`}
            >
              {provider.PhoneNumber}
            </a>
          </li>
        ) : null}
        {filterRestricted(provider.PostAddress) ? (
          <li>
            <span className={classes.listItemText}>{provider.PostAddress}</span>
          </li>
        ) : null}
        {provider.Description && (
          <li style={{ marginTop: 15 }}>
            <span className={classes.listItemText}>{provider.Description}</span>
          </li>
        )}
      </ul>
      {showSubstitute ? (
        <ProviderInfo provider={provider.Substitutes[0]} substitute={true} />
      ) : null}
    </div>
  );
};

export default memo(ProviderInfo);
